import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Anchor } from 'lucide-react';
import '../styles/MasterForms.css';
import apiClient from '../services/api';

const Port = () => {
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;
  const yearId = session?.yearid;

  const [ports, setPorts] = useState([]);
  const [filteredPorts, setFilteredPorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [activeOnly, setActiveOnly] = useState(false);
  
  const [formData, setFormData] = useState({
    PortID: null,
    Port: '',
    PortCode: '',
    IsActive: true
  });
  
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!companyId || !yearId) {
      setLoading(false);
      return;
    }
    fetchPorts();
  }, [companyId, yearId, activeOnly]);

  useEffect(() => {
    filterPorts();
  }, [searchTerm, ports]);

  const fetchPorts = async () => {
    setLoading(true);
    try {
      const params = activeOnly ? { table: 'port', active: '1' } : { table: 'port' };
      const response = await apiClient.get('/LookupMaster.php', { params });
      
      if (response.data.success) {
        setPorts(response.data.data || []);
      } else {
        alert('Failed to fetch ports');
      }
    } catch (error) {
      console.error('Error fetching ports:', error);
      alert('Error fetching ports');
    } finally {
      setLoading(false);
    }
  };

  const filterPorts = () => {
    if (!searchTerm.trim()) {
      setFilteredPorts(ports);
    } else {
      const filtered = ports.filter(port =>
        port.Port.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (port.PortCode && port.PortCode.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredPorts(filtered);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.Port.trim()) {
      newErrors.Port = 'Port name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setSubmitLoading(true);
    try {
      const payload = {
        ...formData,
        IsActive: formData.IsActive ? 1 : 0,
        PortCode: formData.PortCode.trim() || null
      };
      
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/LookupMaster.php?table=port', payload);
      
      if (response.data.success) {
        alert(editMode ? 'Port updated successfully!' : 'Port created successfully!');
        setShowModal(false);
        resetForm();
        fetchPorts();
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error saving port:', error);
      alert('Error saving port: ' + (error.response?.data?.message || error.message));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = async (port) => {
    try {
      const response = await apiClient.get(`/LookupMaster.php?table=port&id=${port.PortID}`);
      if (response.data.success) {
        setFormData({
          PortID: response.data.data.PortID,
          Port: response.data.data.Port,
          PortCode: response.data.data.PortCode || '',
          IsActive: response.data.data.IsActive === 1
        });
        setEditMode(true);
        setShowModal(true);
        setErrors({});
      }
    } catch (error) {
      console.error('Error loading port:', error);
      alert('Error loading port details');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this port?')) return;
    
    try {
      const response = await apiClient.delete('/LookupMaster.php?table=port', {
        data: { PortID: id }
      });
      
      if (response.data.success) {
        alert('Port deleted successfully!');
        fetchPorts();
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error deleting port:', error);
      alert('Error deleting port');
    }
  };

  const resetForm = () => {
    setFormData({ PortID: null, Port: '', PortCode: '', IsActive: true });
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  if (!companyId || !yearId) {
    return (
      <div className="master-container">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>Port Master</h2>
          <p className="subtitle">Manage seaports and airports</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} />
          Add Port
        </button>
      </div>

      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search ports or codes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        
        <div className="filter-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
            />
            <span>Active Only</span>
          </label>
        </div>

        <div className="toolbar-info">
          Total: {filteredPorts.length}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading ports...</div>
      ) : filteredPorts.length === 0 ? (
        <div className="empty-state">
          <p>No ports found</p>
          {searchTerm && <button onClick={() => setSearchTerm('')}>Clear search</button>}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Port Code</th>
                <th>Port Name</th>
                <th>Status</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPorts.map((port) => (
                <tr key={port.PortID}>
                  <td className="font-mono">{port.PortID}</td>
                  <td className="font-mono font-semibold">{port.PortCode || '-'}</td>
                  <td>{port.Port}</td>
                  <td>
                    {port.IsActive === 1 ? (
                      <span className="status-badge active">Active</span>
                    ) : (
                      <span className="status-badge inactive">Inactive</span>
                    )}
                  </td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(port)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(port.PortID)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Port' : 'Add New Port'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-section">
                <h4>
                  <Anchor size={18} />
                  Port Information
                </h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="Port">
                      Port Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="Port"
                      value={formData.Port}
                      onChange={(e) => setFormData({ ...formData, Port: e.target.value })}
                      maxLength="255"
                      placeholder="e.g., Jebel Ali Port, Dubai Airport"
                      className={errors.Port ? 'error' : ''}
                      autoFocus
                    />
                    {errors.Port && (
                      <span className="error-message">{errors.Port}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="PortCode">Port Code</label>
                    <input
                      type="text"
                      id="PortCode"
                      value={formData.PortCode}
                      onChange={(e) => setFormData({ ...formData, PortCode: e.target.value.toUpperCase() })}
                      maxLength="20"
                      placeholder="e.g., JAFZA, DXB"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.IsActive}
                      onChange={(e) => setFormData({ ...formData, IsActive: e.target.checked })}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Port;